<template>
  <div class="vpaa-dashboard pa-10">

    <!-- Header -->
    <div class="d-flex flex-wrap justify-space-between align-center mb-7 gap-3">
      <div>
        <div class="dash-page-title">VPAA Dashboard</div>
        <div class="dash-page-sub mt-1">Institution-wide OJT/SIPP Program Overview</div>
      </div>
      <div class="date-pill rounded-lg px-4 py-2">{{ currentDate }}</div>
    </div>

    <!-- Stat Cards -->
    <v-row class="mb-5" style="gap:0">
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--green rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--green rounded-lg pa-2">
              <v-icon size="22" color="#059669">mdi-account-multiple-outline</v-icon>
            </div>
            <v-icon size="18" color="#059669">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--green">{{ stats.total_students }}</div>
          <div class="stat-label">Total Students</div>
          <div class="stat-sub">Enrolled this academic year</div>
        </div>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--blue rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--blue rounded-lg pa-2">
              <v-icon size="22" color="#2563eb">mdi-briefcase-check-outline</v-icon>
            </div>
            <v-chip size="x-small" color="blue" variant="tonal">{{ stats.deploy_rate }}%</v-chip>
          </div>
          <div class="stat-number stat-number--blue">{{ stats.total_deployed }}</div>
          <div class="stat-label">Deployed / Completed</div>
          <div class="stat-sub">Deployment rate: {{ stats.deploy_rate }}%</div>
        </div>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--amber rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--amber rounded-lg pa-2">
              <v-icon size="22" color="#d97706">mdi-domain</v-icon>
            </div>
            <v-icon size="18" color="#d97706">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--amber">{{ stats.total_htes }}</div>
          <div class="stat-label">Partner HTEs</div>
          <div class="stat-sub">Active host companies</div>
        </div>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--purple rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--purple rounded-lg pa-2">
              <v-icon size="22" color="#7c3aed">mdi-school-outline</v-icon>
            </div>
            <v-icon size="18" color="#7c3aed">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--purple">{{ stats.total_programs }}</div>
          <div class="stat-label">Programs</div>
          <div class="stat-sub">With deployed interns</div>
        </div>
      </v-col>
    </v-row>

    <!-- Deployment by Institute + MOA Summary -->
    <v-row class="mb-5" align="stretch">

      <!-- Deployment bar chart per institute -->
      <v-col cols="12" md="8">
        <div class="dash-card rounded-xl pa-6 h-100">
          <div class="card-title mb-1">Deployment by Institute / College</div>
          <div class="card-sub mb-4">Total students vs deployed per institute</div>
          <div v-if="loading" class="py-4">
            <v-skeleton-loader v-for="n in 4" :key="n" type="list-item" class="mb-2" />
          </div>
          <template v-else-if="instituteStats.length">
            <div
              v-for="(inst, idx) in instituteStats"
              :key="idx"
              :class="idx < instituteStats.length - 1 ? 'mb-5' : ''"
            >
              <div class="d-flex justify-space-between align-center mb-1">
                <span style="font-size:13px; font-weight:600; color:#111827">{{ inst.name }}</span>
                <span style="font-size:12px; color:#6b7280">
                  {{ inst.deployed }}/{{ inst.total }} ({{ inst.deploy_rate }}%)
                </span>
              </div>
              <v-progress-linear
                :model-value="inst.deploy_rate"
                :color="inst.deploy_rate >= 75 ? '#059669' : inst.deploy_rate >= 40 ? '#f59e0b' : '#ef4444'"
                height="8"
                rounded
                bg-color="#f3f4f6"
              />
            </div>
          </template>
          <div v-else class="text-center py-6" style="color:#9ca3af; font-size:13px">
            No institute data available.
          </div>
        </div>
      </v-col>

      <!-- MOA Summary -->
      <v-col cols="12" md="4">
        <div class="dash-card rounded-xl pa-6 h-100">
          <div class="card-title mb-1">MOA Status Summary</div>
          <div class="card-sub mb-4">Partner agreement health</div>
          <div class="chart-wrap chart-wrap--doughnut mb-3">
            <canvas ref="moaChart"></canvas>
          </div>
          <div class="d-flex flex-column" style="gap:6px">
            <div class="d-flex justify-space-between align-center">
              <div class="d-flex align-center ga-2">
                <div class="legend-dot" style="background:#059669"></div>
                <span class="legend-label">Active</span>
              </div>
              <span style="font-size:12px; font-weight:600; color:#111827">{{ moaSummary.active }}</span>
            </div>
            <div class="d-flex justify-space-between align-center">
              <div class="d-flex align-center ga-2">
                <div class="legend-dot" style="background:#f59e0b"></div>
                <span class="legend-label">Expiring Soon</span>
              </div>
              <span style="font-size:12px; font-weight:600; color:#111827">{{ moaSummary.expiring }}</span>
            </div>
            <div class="d-flex justify-space-between align-center">
              <div class="d-flex align-center ga-2">
                <div class="legend-dot" style="background:#ef4444"></div>
                <span class="legend-label">Expired</span>
              </div>
              <span style="font-size:12px; font-weight:600; color:#111827">{{ moaSummary.expired }}</span>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Institute overview table -->
    <div class="dash-card rounded-xl pa-6">
      <div class="card-title mb-1">Institute Overview</div>
      <div class="card-sub mb-4">Deployment statistics per institute / college</div>
      <v-table density="compact">
        <thead>
          <tr>
            <th style="font-size:12px; font-weight:700; color:#374151">Institute / College</th>
            <th class="text-center" style="font-size:12px; font-weight:700; color:#374151">Total Students</th>
            <th class="text-center" style="font-size:12px; font-weight:700; color:#374151">Deployed</th>
            <th class="text-center" style="font-size:12px; font-weight:700; color:#374151">Active HTEs</th>
            <th class="text-center" style="font-size:12px; font-weight:700; color:#374151">Deploy Rate</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!instituteStats.length">
            <td colspan="5" class="text-center py-4" style="color:#9ca3af; font-size:13px">No data available.</td>
          </tr>
          <tr v-for="(inst, idx) in instituteStats" :key="idx">
            <td style="font-size:13px; font-weight:600; color:#111827">{{ inst.name }}</td>
            <td class="text-center" style="font-size:13px">{{ inst.total }}</td>
            <td class="text-center" style="font-size:13px">{{ inst.deployed }}</td>
            <td class="text-center" style="font-size:13px">{{ inst.active_htes }}</td>
            <td class="text-center">
              <v-chip
                size="x-small"
                :color="inst.deploy_rate >= 75 ? 'success' : inst.deploy_rate >= 40 ? 'warning' : 'error'"
                variant="tonal"
              >{{ inst.deploy_rate }}%</v-chip>
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>

    <!-- Recent Activity -->
    <v-row class="mb-5 mt-5">
      <v-col cols="12">
        <div class="dash-card rounded-xl pa-6">
          <div class="d-flex justify-space-between align-center mb-5">
            <div>
              <div class="card-title">Recent Activity</div>
              <div class="card-sub">Latest system events</div>
            </div>
            <router-link to="/vpaa/activity-log" class="activity-view-all-link">View All →</router-link>
          </div>
          <div v-if="activityLoading" class="d-flex justify-center py-6">
            <v-progress-circular indeterminate color="#10b981" size="32" />
          </div>
          <div v-else-if="!recentActivity.length" class="text-center py-6" style="color: #9ca3af; font-size: 13px">
            No recent activity
          </div>
          <div v-else>
            <div
              v-for="(item, idx) in recentActivity"
              :key="idx"
              class="activity-item rounded-xl pa-4"
              :class="idx < recentActivity.length - 1 ? 'mb-3' : ''"
              :style="getActivityBadgeStyle(item.action)"
            >
              <div class="d-flex align-start ga-3">
                <div class="activity-icon-box rounded-lg pa-2 flex-shrink-0" :style="{ background: getActivityConfig(item.action).iconBg }">
                  <v-icon :color="getActivityColor(item.action)" size="18">{{ getActivityIcon(item.action) }}</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex justify-space-between align-start">
                    <div class="activity-title">{{ item.description || item.action }}</div>
                    <v-chip size="x-small" :color="getActivityColor(item.action)" variant="tonal" class="ml-2 flex-shrink-0">{{ getActivityConfig(item.action).label }}</v-chip>
                  </div>
                  <div class="activity-desc">By {{ item.performed_by_name || 'System' }}</div>
                  <div class="activity-time">{{ formatActivityTime(item.created_at) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import Chart from "chart.js/auto";
export default {
  name: "VPAADashboard",
  data() {
    return {
      loading: false,
      charts: {},
      recentActivity: [],
      activityLoading: false,
      stats: {
        total_students: 0,
        total_deployed: 0,
        total_htes: 0,
        total_programs: 0,
        deploy_rate: 0,
      },
      instituteStats: [],
      moaSummary: { active: 0, expiring: 0, expired: 0 },
    };
  },
  computed: {
    currentDate() {
      return new Date().toLocaleDateString("en-US", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      });
    },
  },
  mounted() {
    this.fetchData();
    this.fetchRecentActivity();
  },
  beforeUnmount() {
    Object.values(this.charts).forEach((c) => c && c.destroy());
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const res = await this.axiosCall("/student/vpaa-dashboard", "GET");
        if (res?.data) {
          const d = res.data;
          if (d.stats)           this.stats = { ...this.stats, ...d.stats };
          if (d.institute_stats) this.instituteStats = d.institute_stats;
          if (d.moa_summary)     this.moaSummary = d.moa_summary;
        }
      } catch (e) {
        console.error("Failed to load VPAA dashboard", e);
      } finally {
        this.loading = false;
        this.$nextTick(() => this.initMoaChart());
      }
    },

    initMoaChart() {
      if (this.charts.moa) this.charts.moa.destroy();
      if (!this.$refs.moaChart) return;
      const { active, expiring, expired } = this.moaSummary;
      this.charts.moa = new Chart(this.$refs.moaChart, {
        type: "doughnut",
        data: {
          labels: ["Active", "Expiring Soon", "Expired"],
          datasets: [{
            data: [active || 0, expiring || 0, expired || 0],
            backgroundColor: ["#059669", "#f59e0b", "#ef4444"],
            borderWidth: 3,
            borderColor: "#ffffff",
            hoverOffset: 6,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "65%",
          plugins: { legend: { display: false } },
        },
      });
    },

    async fetchRecentActivity() {
      this.activityLoading = true;
      try {
        const res = await this.axiosCall('/system-logs?page=1&limit=5', 'GET');
        if (res?.data?.data) this.recentActivity = res.data.data;
      } catch (e) {
        console.error('Failed to load recent activity', e);
      } finally {
        this.activityLoading = false;
      }
    },
    getActivityConfig(action) {
      const map = {
        USER_LOGIN:              { color: '#10b981', iconBg: '#d1fae5', icon: 'mdi-login',                   label: 'Login'      },
        USER_LOGOUT:             { color: '#6b7280', iconBg: '#f3f4f6', icon: 'mdi-logout',                  label: 'Logout'     },
        BATCH_CREATED:           { color: '#8b5cf6', iconBg: '#ede9fe', icon: 'mdi-layers-plus',             label: 'Batch'      },
        STUDENT_ENDORSED:        { color: '#3b82f6', iconBg: '#dbeafe', icon: 'mdi-account-arrow-right',     label: 'Endorsed'   },
        STUDENT_DEPLOYED:        { color: '#10b981', iconBg: '#d1fae5', icon: 'mdi-briefcase-check-outline', label: 'Deployed'   },
        STUDENT_PULLED_OUT:      { color: '#ef4444', iconBg: '#fee2e2', icon: 'mdi-account-minus-outline',   label: 'Pulled Out' },
        STUDENT_REASSIGNED:      { color: '#f59e0b', iconBg: '#fef3c7', icon: 'mdi-account-switch-outline',  label: 'Reassigned' },
        REQUIREMENT_SUBMITTED:   { color: '#3b82f6', iconBg: '#dbeafe', icon: 'mdi-file-upload-outline',     label: 'Submitted'  },
        REQUIREMENT_APPROVED:    { color: '#10b981', iconBg: '#d1fae5', icon: 'mdi-file-check-outline',      label: 'Approved'   },
        REQUIREMENT_DISAPPROVED: { color: '#ef4444', iconBg: '#fee2e2', icon: 'mdi-file-cancel-outline',     label: 'Returned'   },
        ATTENDANCE_SCANNED:      { color: '#06b6d4', iconBg: '#cffafe', icon: 'mdi-qrcode-scan',             label: 'Attendance' },
      };
      return map[action] || { color: '#6b7280', iconBg: '#f3f4f6', icon: 'mdi-information-outline', label: action };
    },
    getActivityColor(action) { return this.getActivityConfig(action).color; },
    getActivityIcon(action)  { return this.getActivityConfig(action).icon; },
    getActivityBadgeStyle(action) {
      const c = this.getActivityConfig(action);
      return { background: c.iconBg, border: `1px solid ${c.color}22` };
    },
    formatActivityTime(dateStr) {
      if (!dateStr) return '';
      const diff  = Date.now() - new Date(dateStr).getTime();
      const mins  = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days  = Math.floor(diff / 86400000);
      if (mins  < 1)  return 'just now';
      if (mins  < 60) return `${mins}m ago`;
      if (hours < 24) return `${hours}h ago`;
      return `${days}d ago`;
    },
  },
};
</script>

<style scoped>
.vpaa-dashboard { min-height: 100vh; }

.dash-page-title { font-size: 30px; font-weight: 800; color: #111827; letter-spacing: -0.5px; line-height: 1.2; }
.dash-page-sub   { font-size: 14px; color: #6b7280; }
.date-pill       { background: #f3f4f6; font-size: 13px; font-weight: 500; color: #374151; }

.stat-card { border: 1px solid transparent; transition: box-shadow 0.2s; height: 100%; }
.stat-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,.09); }
.stat-card--green  { background: #ecfdf5; border-color: #a7f3d0; }
.stat-card--blue   { background: #eff6ff; border-color: #bfdbfe; }
.stat-card--amber  { background: #fffbeb; border-color: #fde68a; }
.stat-card--purple { background: #f5f3ff; border-color: #ddd6fe; }

.stat-icon-box { display: inline-flex; align-items: center; justify-content: center; }
.stat-icon-box--green  { background: #a7f3d0; }
.stat-icon-box--blue   { background: #bfdbfe; }
.stat-icon-box--amber  { background: #fde68a; }
.stat-icon-box--purple { background: #ddd6fe; }

.stat-number { font-size: 40px; font-weight: 800; line-height: 1.05; letter-spacing: -1.5px; }
.stat-number--green  { color: #059669; }
.stat-number--blue   { color: #2563eb; }
.stat-number--amber  { color: #d97706; }
.stat-number--purple { color: #7c3aed; }

.stat-label { font-size: 13px; font-weight: 600; color: #374151; margin-top: 6px; }
.stat-sub   { font-size: 11.5px; color: #9ca3af; margin-top: 3px; }

.dash-card  { background: #fff; border: 1px solid #d1d5db; box-shadow: 0 1px 6px rgba(0,0,0,.06); }
.card-title { font-size: 15px; font-weight: 700; color: #111827; }
.card-sub   { font-size: 12.5px; color: #9ca3af; }

.chart-wrap { position: relative; width: 100%; height: 220px; }
.chart-wrap--doughnut { height: 160px; }

.legend-dot   { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.legend-label { font-size: 12px; color: #6b7280; }

.activity-view-all-link { font-size: 13px; font-weight: 600; color: #10b981; text-decoration: none; }
.activity-view-all-link:hover { text-decoration: underline; }
.activity-item { transition: box-shadow 0.15s; }
.activity-icon-box { display: inline-flex; }
.activity-title { font-size: 13px; font-weight: 600; color: #111827; }
.activity-desc  { font-size: 12px; color: #6b7280; margin-top: 2px; }
.activity-time  { font-size: 11px; color: #9ca3af; margin-top: 4px; }
.card-sub { font-size: 12.5px; color: #9ca3af; }
</style>
